import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Pagination from './Pagination';
import { Search, ArrowDownward, ArrowUpward } from '@mui/icons-material';
import TableLoader from '../TableLoader';

const ALIGNMENT_CLASSES = {
  end: 'text-end',
  center: 'text-center',
  start: 'text-start',
};

const CommonTable = ({ data, columns, align = 'start', loading, footerRow }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [currentData, setCurrentData] = useState([]);

  const getAlignmentClass = useCallback(
    (columnAlign) => ALIGNMENT_CLASSES[columnAlign || align] || 'text-start',
    [align]
  );

  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return data;
    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue == null || aValue === '') return 1;
      if (bValue == null || bValue === '') return -1;

      const aType = typeof aValue;
      const bType = typeof bValue;

      if (aType === 'number' && bType === 'number') {
        return sortConfig.direction === 'ascending' ? aValue - bValue : bValue - aValue;
      } else if (aType === 'string' && bType === 'string') {
        return sortConfig.direction === 'ascending'
          ? aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: 'base' })
          : bValue.localeCompare(aValue, undefined, { numeric: true, sensitivity: 'base' });
      } else {
        const aString = String(aValue);
        const bString = String(bValue);
        return sortConfig.direction === 'ascending'
          ? aString.localeCompare(bString, undefined, { numeric: true, sensitivity: 'base' })
          : bString.localeCompare(aString, undefined, { numeric: true, sensitivity: 'base' });
      }
    });
  }, [data, sortConfig]);

  const filteredData = useMemo(() => {
    return sortedData?.filter((item) =>
      columns.some((column) => String(item[column.accessor]).toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [sortedData, columns, searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortConfig]);

  useEffect(() => {
    setSearchTerm('');
    setCurrentPage(1);
  }, [data]);

  const requestSort = useCallback((key, sorting) => {
    if (sorting === false) return;

    setSortConfig((prevConfig) => {
      if (prevConfig.key === key) {
        if (prevConfig.direction === 'ascending') {
          return { key, direction: 'descending' };
        } else if (prevConfig.direction === 'descending') {
          return { key: null, direction: null };
        }
      }
      return { key, direction: 'ascending' };
    });
  }, []);

  const renderSortIcon = useCallback(
    (accessor) => {
      if (sortConfig.key !== accessor)
        return (
          <>
            <ArrowUpward fontSize="small" />
            <ArrowDownward fontSize="small" />
          </>
        );
      return sortConfig.direction === 'ascending' ? (
        <ArrowUpward fontSize="small" />
      ) : (
        <ArrowDownward fontSize="small" />
      );
    },
    [sortConfig]
  );

  const renderHeaders = useCallback(() => {
    return columns.map(({ label, accessor, align: columnAlign, sorting }) => (
      <th key={accessor} className={getAlignmentClass(columnAlign)}>
        <div
          className={`d-flex align-items-center ${sorting === false ? '' : 'cursor-pointer'}`}
          onClick={() => requestSort(accessor, sorting)}
        >
          {label}
          {sorting !== false && <span className="ms-1">{renderSortIcon(accessor)}</span>}
        </div>
      </th>
    ));
  }, [columns, getAlignmentClass, requestSort, renderSortIcon]);

  const renderCell = useCallback(
    (value, row, rowIndex, colIndex, { accessor, render, align: columnAlign }) => {
      const isEmptyValue =
        value === undefined || value === null || value === '' || value === 'undefined' || value === 'null';
      const renderedValue = render ? render(value, row, rowIndex, colIndex) : isEmptyValue ? '-' : value;

      return (
        <td key={`${rowIndex}-${accessor}`} className={getAlignmentClass(columnAlign)}>
          {renderedValue}
        </td>
      );
    },
    [getAlignmentClass]
  );

  const renderRows = useCallback(() => {
    return currentData.map((row, rowIndex) => (
      <tr key={rowIndex}>
        {columns.map((column, colIndex) => renderCell(row[column.accessor], row, rowIndex, colIndex, column))}
      </tr>
    ));
  }, [columns, currentData, renderCell]);

  return (
    <div>
      <div className="input-group my-3 search_input ms-auto">
        <span className="input-group-text">
          <Search />
        </span>
        <input
          type="text"
          className="form-control"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search..."
        />
      </div>
      <div className="table-responsive border rounded-3 bg-white border-bottom-0">
        {loading ? (
          <TableLoader />
        ) : (
          <table className="table mb-0">
            <thead className="text-nowrap">
              <tr className="base-color-bg text-white">{renderHeaders()}</tr>
            </thead>
            <tbody>
              {filteredData?.length > 0 ? (
                renderRows()
              ) : (
                <tr>
                  <td colSpan={columns.length} className="text-center">
                    No Data Found
                  </td>
                </tr>
              )}
            </tbody>
            {footerRow && (
              <tfoot>
                <tr>
                  <td colSpan={footerRow.colspan} className="fw-bold text-end">
                    {footerRow.label}
                  </td>
                  <td className="fw-bold">{footerRow.value}</td>
                </tr>
              </tfoot>
            )}
          </table>
        )}
      </div>

      {filteredData?.length > 0 && (
        <Pagination
          setCurrentData={setCurrentData}
          currentData={currentData}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          data={filteredData}
        />
      )}
    </div>
  );
};

export default CommonTable;
